import React, { useState, useEffect } from 'react';
import QRCodeLib from 'qrcode';
import { useDomains } from '../../hooks/useDomains';
import { getLinkDomain, buildShortUrl } from '../../utils/domainUtils';

const ModernQRModal = ({ link, isOpen, onClose }) => {
  const { domains } = useDomains();
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrSize, setQrSize] = useState(300);
  const [isGenerating, setIsGenerating] = useState(false);

  // Use dynamic domain resolution
  const domainName = getLinkDomain(link, domains);
  const shortUrl = buildShortUrl(link?.shortCode || '', domainName);

  // Generate QR code when modal opens
  useEffect(() => {
    if (isOpen && link && shortUrl) {
      generateQRCode();
    }
  }, [isOpen, link, shortUrl, qrSize]);

  const generateQRCode = async () => {
    try {
      setIsGenerating(true);
      console.log('🔄 Generating QR code for:', shortUrl);

      const dataUrl = await QRCodeLib.toDataURL(shortUrl, {
        width: qrSize,
        margin: 2,
        color: {
          dark: '#1a1a1a',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'M'
      });

      setQrDataUrl(dataUrl);
      console.log('✅ QR code generated successfully');
    } catch (error) {
      console.error('❌ Error generating QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrDataUrl) return;

    const downloadLink = document.createElement('a');
    downloadLink.download = `qr-${link?.shortCode || 'link'}.png`;
    downloadLink.href = qrDataUrl;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const copyQRCode = async () => {
    if (!qrDataUrl) return;

    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();

      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);

      alert('✅ QR code copied to clipboard!');
    } catch (err) {
      console.error('❌ Error copying QR code:', err);
      alert('❌ Could not copy QR code. Try downloading instead.');
    }
  };

  const copyShortUrl = async () => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      alert('✅ Short URL copied to clipboard!');
    } catch (err) {
      alert('❌ Could not copy URL.');
    }
  };

  if (!isOpen || !link) return null;

  return (
    <div className="qr-modal-overlay" onClick={onClose}>
      <div className="qr-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📱 Share & QR Code</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Link Info */}
          <div className="link-section">
            <div className="link-details">
              <h4>{link.title || 'Untitled Link'}</h4>
              <div className="url-display">
                <code>{shortUrl}</code>
                <button className="copy-url-btn" onClick={copyShortUrl}>
                  📋 Copy URL
                </button>
              </div>
              {link.description && (
                <p className="link-description">{link.description}</p>
              )}
            </div>
          </div>

          {/* QR Code Section */}
          <div className="qr-section">
            <div className="qr-container">
              {isGenerating ? (
                <div className="qr-loading">
                  <div className="spinner"></div>
                  <p>Generating QR code...</p>
                </div>
              ) : qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="QR Code"
                  className="qr-image"
                  style={{ width: qrSize, height: qrSize }}
                />
              ) : (
                <div className="qr-error">
                  <p>❌ Could not generate QR code</p>
                </div>
              )}
            </div>

            {/* QR Controls */}
            <div className="qr-controls">
              <div className="size-control">
                <label>Size:</label>
                <select
                  value={qrSize}
                  onChange={(e) => setQrSize(parseInt(e.target.value))}
                  className="size-select"
                >
                  <option value={200}>Small (200px)</option>
                  <option value={300}>Medium (300px)</option>
                  <option value={400}>Large (400px)</option>
                  <option value={500}>XL (500px)</option>
                </select>
              </div>

              <div className="qr-actions">
                <button
                  className="action-btn secondary"
                  onClick={copyQRCode}
                  disabled={!qrDataUrl || isGenerating}
                >
                  📋 Copy QR
                </button>
                <button
                  className="action-btn primary"
                  onClick={downloadQRCode}
                  disabled={!qrDataUrl || isGenerating}
                >
                  📥 Download
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .qr-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
        }

        .qr-modal {
          background: white;
          border-radius: 20px;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
        }

        .modal-header {
          padding: 24px 30px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f9fafb;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 20px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #6b7280;
          padding: 8px;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .modal-content {
          padding: 30px;
        }

        .link-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .link-details h4 {
          margin: 0 0 12px 0;
          font-size: 18px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .url-display {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #f9fafb;
          padding: 12px 16px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          margin-bottom: 12px;
        }

        .url-display code {
          flex: 1;
          font-family: 'Monaco', 'Menlo', monospace;
          color: #667eea;
          font-size: 14px;
          background: none;
        }

        .copy-url-btn {
          background: #667eea;
          color: white;
          border: none;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .copy-url-btn:hover {
          background: #5a6fd8;
          transform: translateY(-1px);
        }

        .link-description {
          color: #6b7280;
          font-size: 14px;
          line-height: 1.5;
          margin: 0;
        }

        .qr-section {
          text-align: center;
        }

        .qr-container {
          margin-bottom: 24px;
          display: flex;
          justify-content: center;
        }

        .qr-image {
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
          border: 1px solid #e5e7eb;
        }

        .qr-loading,
        .qr-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 200px;
          background: #f9fafb;
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          color: #6b7280;
        }

        .qr-loading .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #f3f4f6;
          border-top: 3px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 12px;
        }

        .qr-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        }

        .size-control {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #374151;
        }

        .size-select {
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .qr-actions {
          display: flex;
          gap: 12px;
        }

        .action-btn {
          padding: 10px 16px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .action-btn.primary {
          background: #667eea;
          color: white;
        }

        .action-btn.primary:hover:not(:disabled) {
          background: #5a6fd8;
          transform: translateY(-1px);
        }

        .action-btn.secondary {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .action-btn.secondary:hover:not(:disabled) {
          background: #e5e7eb;
          transform: translateY(-1px);
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 480px) {
          .qr-modal {
            margin: 10px;
            max-width: none;
          }

          .modal-header,
          .modal-content {
            padding: 20px;
          }

          .qr-controls {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }

          .qr-actions {
            width: 100%;
          }

          .action-btn {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ModernQRModal;